import { useState } from "react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Check } from "@phosphor-icons/react";

const PRESETS = [
  "Details do not match our records",
  "Incomplete or missing information",
  "Profile photo unclear or missing",
  "Duplicate application",
  "Supporting documents required",
];

export function RejectDialog({ open, onOpenChange, onConfirm, name, title = "Reject application" }) {
  const [selected, setSelected] = useState([]);
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);

  const reset = () => { setSelected([]); setNote(""); };
  const toggle = (p) => setSelected((s) => (s.includes(p) ? s.filter((x) => x !== p) : [...s, p]));
  const composed = [...selected, note.trim() ? note.trim() : null].filter(Boolean).join(" — ");
  const canSubmit = selected.length > 0 || note.trim().length > 0;

  const submit = async () => {
    setBusy(true);
    try {
      await onConfirm(composed);
      reset();
      onOpenChange(false);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) reset(); onOpenChange(o); }}>
      <DialogContent className="max-w-md" data-testid="reject-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-xl">{title}</DialogTitle>
          <DialogDescription>
            Pick one or more reasons and add a note. This is emailed to {name || "the member"}.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-2 mt-2">
          <Label>Reasons</Label>
          <div className="flex flex-wrap gap-2">
            {PRESETS.map((p) => {
              const on = selected.includes(p);
              return (
                <button key={p} type="button" onClick={() => toggle(p)}
                  data-testid={`reject-preset-${p.split(" ")[0].toLowerCase()}`}
                  className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-xs font-medium transition-colors ${
                    on ? "bg-destructive text-destructive-foreground border-destructive"
                       : "border-border text-muted-foreground hover:border-destructive/50 hover:text-foreground"}`}>
                  {on && <Check size={12} weight="bold" />} {p}
                </button>
              );
            })}
          </div>
        </div>
        <div className="space-y-1.5 mt-3">
          <Label htmlFor="reject-note">Additional note (optional)</Label>
          <Textarea id="reject-note" data-testid="reject-reason-input" rows={3}
            value={note} onChange={(e) => setNote(e.target.value)}
            placeholder="e.g. Date of birth does not match records; please correct and resubmit." />
        </div>
        <DialogFooter className="mt-2">
          <Button variant="ghost" onClick={() => onOpenChange(false)} data-testid="reject-cancel">Cancel</Button>
          <Button disabled={busy || !canSubmit} onClick={submit} data-testid="reject-confirm"
            className="bg-destructive text-destructive-foreground hover:bg-destructive/90">
            {busy ? "Rejecting…" : "Confirm rejection"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
